// Listado paginado de ComprobanteContable: filtros tipo/syncStatus/rango
// fechas, paginacion server-side via page/pageSize. Acciones por fila:
//   - Ver detalle (siempre): abre modal con historial de sync (intentos,
//     errores, payload enviado).
//   - Reintentar sync: solo si syncStatus === SYNC_ERROR o
//     SYNC_NO_CONFIGURADO. En SYNC_ERROR muestra inline el ultimoErrorSync.

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import { FaReceipt, FaSync, FaSearch, FaEye, FaExclamationTriangle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import moment from 'moment';
import { Alertassw } from '../Alertas/Alertassw';
import { GET } from '../../api/common';
import { forzarSyncComprobante, historialSyncComprobante } from '../../api/finanzas';

const TIPO_BADGE = {
  EGRESO_ENTREGA:  { bg: 'primary',  label: 'Entrega' },
  EGRESO_TRASLADO: { bg: 'info',     label: 'Traslado' },
  EGRESO_CIERRE:   { bg: 'success',  label: 'Cierre' },
  NOTA_CREDITO:    { bg: 'secondary', label: 'N. Crédito' },
  NOTA_DEBITO:     { bg: 'dark',     label: 'N. Débito' }
};

const SYNC_BADGE = {
  PENDIENTE_SYNC:      { bg: 'secondary', label: 'Pendiente' },
  SINCRONIZANDO:       { bg: 'info',      label: 'Sincronizando' },
  SINCRONIZADO:        { bg: 'success',   label: 'Sincronizado' },
  SYNC_ERROR:          { bg: 'danger',    label: 'Error' },
  SYNC_NO_CONFIGURADO: { bg: 'warning',   label: 'Sin proveedor' },
  NO_REQUIERE_SYNC:    { bg: 'light text-dark', label: 'No requiere' }
};

const ESTADO_BADGE = {
  BORRADOR: { bg: 'warning', label: 'Borrador' },
  APROBADO: { bg: 'success', label: 'Aprobado' },
  ANULADO:  { bg: 'dark',    label: 'Anulado' }
};

const formatoMoneda = (n) => {
  if (n === null || n === undefined) return '$ 0';
  return '$ ' + Number(n).toLocaleString('es-CO');
};

/** Limpia el objeto antes de pasarlo a GET (quita valores vacios). */
const limpiarParams = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== '' && v !== null && v !== undefined) out[k] = v;
  }
  return out;
};

const ComprobantesContables = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const [filtros, setFiltros] = useState({
    tipo: '',
    syncStatus: '',
    desde: '',
    hasta: ''
  });

  // Filas con la sección de error expandida (mapa id -> true)
  const [erroresExpandidos, setErroresExpandidos] = useState({});

  // Modal de detalle (historial de sync)
  const [detalle, setDetalle] = useState({ visible: false, cargando: false, data: null, comprobante: null });

  const cargar = async (pageActual = page) => {
    setCargando(true);
    setError(null);
    try {
      const params = limpiarParams({
        page: pageActual,
        pageSize,
        ...filtros
      });
      const resp = await GET('/ComprobanteContableOps', params);
      const data = resp?.data || {};
      // Backend devuelve { items, total, page, pageSize, totalPages } en modo paginado
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || pageActual);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || err.message || 'Error al cargar comprobantes');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAplicarFiltros = () => {
    setPage(1);
    cargar(1);
  };

  const handleLimpiarFiltros = () => {
    setFiltros({ tipo: '', syncStatus: '', desde: '', hasta: '' });
    setPage(1);
    setTimeout(() => cargar(1), 0);
  };

  const handleReintentarSync = async (id, consecutivo) => {
    const r = await Swal.fire({
      icon: 'question',
      title: '¿Reintentar sincronización?',
      text: `El comprobante ${consecutivo} se enviará al software contable nuevamente.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, reintentar',
      cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    try {
      await forzarSyncComprobante(id);
      Alertassw('success', 'Reintentado', `El comprobante ${consecutivo} se reintentó.`);
      cargar(page);
    } catch (err) {
      Alertassw('error', 'Error', err?.response?.data?.error || err.message);
    }
  };

  const toggleError = (id) => {
    setErroresExpandidos((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleVerDetalle = async (comp) => {
    setDetalle({ visible: true, cargando: true, data: null, comprobante: comp });
    try {
      const resp = await historialSyncComprobante(comp._id);
      setDetalle({ visible: true, cargando: false, data: resp?.data?.data || null, comprobante: comp });
    } catch (err) {
      Alertassw('error', 'Error', err?.response?.data?.error || err.message);
      setDetalle({ visible: false, cargando: false, data: null, comprobante: null });
    }
  };

  const cerrarDetalle = () => setDetalle({ visible: false, cargando: false, data: null, comprobante: null });

  const irAPagina = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    cargar(p);
  };

  // Reintentar tiene sentido si la sync fallo o nunca fue posible configurarla.
  const puedeReintentar = (s) => s === 'SYNC_ERROR' || s === 'SYNC_NO_CONFIGURADO';

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-3">
        <Col>
          <h2 className="text-primary fw-bold">
            <FaReceipt className="me-2" />
            Comprobantes Contables
          </h2>
          <p className="text-muted">
            Listado de comprobantes generados por entregas, traslados y cierre de gastos. Reintenta los que están en error.
          </p>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="shadow-sm mb-3 w-100">
        <Card.Body className='w-100'>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label>Tipo</Form.Label>
              <Form.Select
                value={filtros.tipo}
                onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="EGRESO_ENTREGA">Entrega</option>
                <option value="EGRESO_TRASLADO">Traslado</option>
                <option value="EGRESO_CIERRE">Cierre de gastos</option>
                <option value="NOTA_CREDITO">Nota crédito</option>
                <option value="NOTA_DEBITO">Nota débito</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Estado de sync</Form.Label>
              <Form.Select
                value={filtros.syncStatus}
                onChange={(e) => setFiltros({ ...filtros, syncStatus: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="PENDIENTE_SYNC">Pendiente</option>
                <option value="SINCRONIZANDO">Sincronizando</option>
                <option value="SINCRONIZADO">Sincronizado</option>
                <option value="SYNC_ERROR">Error</option>
                <option value="SYNC_NO_CONFIGURADO">Sin proveedor</option>
                <option value="NO_REQUIERE_SYNC">No requiere</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                value={filtros.desde}
                onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                value={filtros.hasta}
                onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
              />
            </Col>
            <Col md={2}>
              <Button variant="primary" onClick={handleAplicarFiltros} className="me-2">
                <FaSearch className="me-1" /> Filtrar
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={handleLimpiarFiltros}>
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla */}
      <Card className="shadow-sm w-100">
        <Card.Body className='w-100'>
          {cargando && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}

          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}

          {!cargando && !error && items.length === 0 && (
            <Alert variant="light" className="text-center mb-0">
              No hay comprobantes que coincidan con los filtros.
            </Alert>
          )}

          {!cargando && items.length > 0 && (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Consecutivo</th>
                      <th>Tipo</th>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th className="text-end">Valor</th>
                      <th>Estado</th>
                      <th>Sync</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => {
                      const tipo = TIPO_BADGE[c.tipoComprobante] || { bg: 'secondary', label: c.tipoComprobante };
                      const sync = SYNC_BADGE[c.syncStatus] || { bg: 'secondary', label: c.syncStatus };
                      const est = ESTADO_BADGE[c.estado] || { bg: 'secondary', label: c.estado };
                      const conError = c.syncStatus === 'SYNC_ERROR' && c.ultimoErrorSync;
                      const expandido = !!erroresExpandidos[c._id];
                      return (
                        <React.Fragment key={c._id}>
                          <tr>
                            <td><code>{c.consecutivo}</code></td>
                            <td><Badge bg={tipo.bg}>{tipo.label}</Badge></td>
                            <td>{moment(c.fecha).format('DD/MM/YYYY')}</td>
                            <td className="text-truncate" style={{ maxWidth: 280 }}>{c.concepto}</td>
                            <td className="text-end">{formatoMoneda(c.valorTotal)}</td>
                            <td><Badge bg={est.bg}>{est.label}</Badge></td>
                            <td>
                              <Badge bg={sync.bg}>{sync.label}</Badge>
                              {conError && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 ms-2 text-danger"
                                  onClick={() => toggleError(c._id)}
                                  title={expandido ? 'Ocultar error' : 'Ver error de sincronización'}
                                >
                                  <FaExclamationTriangle />
                                </Button>
                              )}
                              {typeof c.intentosSincronizacion === 'number' && c.intentosSincronizacion > 0 && (
                                <small className="text-muted ms-2">({c.intentosSincronizacion} int.)</small>
                              )}
                            </td>
                            <td className="text-center text-nowrap">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-1"
                                onClick={() => handleVerDetalle(c)}
                                title="Ver detalle e historial de sincronización"
                              >
                                <FaEye />
                              </Button>
                              {puedeReintentar(c.syncStatus) && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleReintentarSync(c._id, c.consecutivo)}
                                  title="Reintentar sincronización"
                                >
                                  <FaSync />
                                </Button>
                              )}
                            </td>
                          </tr>
                          {conError && expandido && (
                            <tr>
                              <td colSpan={8} className="bg-light-subtle">
                                <Alert variant="danger" className="mb-0 small">
                                  <strong>Último error de sincronización</strong>
                                  {c.fechaSincronizacion && (
                                    <span className="ms-2 text-muted">
                                      ({moment(c.fechaSincronizacion).format('DD/MM/YYYY HH:mm')})
                                    </span>
                                  )}
                                  <div className="mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {c.ultimoErrorSync}
                                  </div>
                                </Alert>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Paginación */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Mostrando {items.length} de {total} comprobantes — página {page} de {totalPages}
                </small>
                <div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="me-2"
                    onClick={() => irAPagina(page - 1)}
                    disabled={page <= 1 || cargando}
                  >
                    ← Anterior
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => irAPagina(page + 1)}
                    disabled={page >= totalPages || cargando}
                  >
                    Siguiente →
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal de detalle / historial de sincronización */}
      <Modal show={detalle.visible} onHide={cerrarDetalle} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            Detalle del comprobante
            {detalle.comprobante?.consecutivo && (
              <code className="ms-2">{detalle.comprobante.consecutivo}</code>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detalle.cargando && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}

          {!detalle.cargando && detalle.data && (() => {
            const d = detalle.data;
            const sync = SYNC_BADGE[d.syncStatus] || { bg: 'secondary', label: d.syncStatus };
            return (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <small className="text-muted d-block">Estado de sincronización</small>
                    <Badge bg={sync.bg}>{sync.label}</Badge>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Intentos</small>
                    <span>{d.intentosSincronizacion ?? 0}</span>
                  </Col>
                </Row>

                {d.fechaSincronizacion && (
                  <div className="mb-2">
                    <small className="text-muted d-block">Última sincronización</small>
                    <span>{moment(d.fechaSincronizacion).format('DD/MM/YYYY HH:mm:ss')}</span>
                  </div>
                )}

                {d.externalId && (
                  <div className="mb-2">
                    <small className="text-muted d-block">ID externo</small>
                    <code>{d.externalId}</code>
                  </div>
                )}

                {d.ultimoErrorSync && (
                  <Alert variant="danger" className="small">
                    <strong>Último error de sincronización:</strong>
                    <div className="mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {d.ultimoErrorSync}
                    </div>
                  </Alert>
                )}

                {d.payloadEnviado && (
                  <details className="mb-2">
                    <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                      Payload enviado al ERP
                    </summary>
                    <pre className="bg-light p-2 small mb-0" style={{ maxHeight: 200, overflow: 'auto' }}>
                      {JSON.stringify(d.payloadEnviado, null, 2)}
                    </pre>
                  </details>
                )}

                {d.responseExterna && (
                  <details className="mb-2">
                    <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                      Respuesta del ERP
                    </summary>
                    <pre className="bg-light p-2 small mb-0" style={{ maxHeight: 200, overflow: 'auto' }}>
                      {JSON.stringify(d.responseExterna, null, 2)}
                    </pre>
                  </details>
                )}
              </>
            );
          })()}

          {!detalle.cargando && !detalle.data && (
            <Alert variant="light">No hay detalle de sincronización disponible.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {detalle.comprobante && puedeReintentar(detalle.comprobante.syncStatus) && (
            <Button
              variant="warning"
              onClick={() => {
                const { _id, consecutivo } = detalle.comprobante;
                cerrarDetalle();
                handleReintentarSync(_id, consecutivo);
              }}
            >
              <FaSync className="me-1" /> Reintentar sincronización
            </Button>
          )}
          <Button variant="secondary" onClick={cerrarDetalle}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ComprobantesContables;
